import { useState } from "react";
import { ActionModal } from "@cntrl/ui";
import { useCreateGroupThread } from "../../app/hooks/useInbox.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { MessageablePicker } from "./MessageablePicker.js";
import styles from "./Inbox.module.scss";

export interface NewThreadModalProps {
  /** Exactly ONE person was picked — hand them back for a DRAFT. Nothing is created; see below. */
  onPickedOne: (personId: string) => void;
  /** A GROUP chat was created — the caller routes to it. */
  onCreatedGroup: (threadId: string) => void;
  /**
   * May this caller start a group chat (the served `canCreateGroupChat`)? When false the picker is
   * single-select — a second pick would only ever be refused by the server, and a control whose outcome is a
   * refusal is the dead button this codebase refuses.
   */
  canCreateGroup: boolean;
  onClose: () => void;
}

/**
 * START SOMETHING — ONE door, and how many people you pick decides what it is (PO, 2026-09-11).
 *
 * **One button, not two.** This briefly had a "+ New" beside a "Group", which made the reader choose the
 * SHAPE of a conversation before choosing who is in it — the wrong order, and a decision they do not have to
 * make: picking one person means a DM and picking several means a group.
 *
 * **The picker is a sectioned CHECKLIST** (`MessageablePicker`, 2026-09-13), not the old type-ahead chip
 * control: a reader wants to SEE who they can write to and each person's role and org, grouped, rather than
 * type a name and hope. `max` is the one-vs-many knob — a seat that cannot start a group is single-select.
 *
 * **NO NAME AT CREATION (PO, 2026-09-13).** A group used to require a name here. It no longer does: an unnamed
 * group is named for its members, excluding the reader ("John and Edward"), and can be renamed later from the
 * chat header. So there is nothing to type — pick people and confirm.
 *
 * **The list is the gate read forwards.** `GET /me/messageable` and both create paths resolve through
 * `core.messageable_people`, so every option here is one the server will accept. Building a picker from a
 * roster beside the gate is the ART-122 defect, where the offered set and the accepted set disagreed in both
 * directions — an affordance that is refused and a permission that is unreachable are the same bug twice.
 *
 * **PICKING ONE PERSON CREATES NOTHING (ART-159: an abandoned DM "should go away when you navigate away").**
 * The one-person path hands the id back and the thread is written by the first MESSAGE. A group IS created on
 * confirm, because it has a roster other people are about to be told about — something to save before anybody
 * speaks, where an abandoned DM is a row nobody asked for.
 */
export function NewThreadModal({
  onPickedOne,
  onCreatedGroup,
  canCreateGroup,
  onClose,
}: NewThreadModalProps) {
  const [picked, setPicked] = useState<string[]>([]);
  const create = useCreateGroupThread();

  const isGroup = picked.length > 1;
  const ready = picked.length >= 1;

  function submit() {
    if (picked.length === 1) {
      onPickedOne(picked[0]!);
      return onClose();
    }
    if (!isGroup) return;
    // No title — an unnamed group is named for its members and renamed later (PO, 2026-09-13).
    create.mutate(
      { memberPersonIds: picked },
      {
        onSuccess: (thread) => {
          const id = thread.threadId as string | undefined;
          if (id) onCreatedGroup(id);
          onClose();
        },
      },
    );
  }

  return (
    <ActionModal
      title="New message"
      // The label says what the button will DO, which is the only place the one-vs-many distinction needs
      // explaining. A fixed "Next" would leave the reader to work out whether a group is about to exist.
      confirmLabel={isGroup ? "Create group" : "Message"}
      loading={create.isPending}
      confirmDisabled={!ready}
      size="md"
      onCancel={onClose}
      onConfirm={submit}
    >
      {/* A caller who cannot start a group is single-select: the LAST pick wins (a swap), because refusing
          the pick would read as a broken control and a second selection would build a request the server
          rejects. `max` lives on the picker so that rule has one home. */}
      <MessageablePicker value={picked} onChange={setPicked} max={canCreateGroup ? undefined : 1} />

      {create.isError && (
        <p className={styles.dialogError} role="alert">
          {errorMessage(create.error, "Couldn't start the chat.")}
        </p>
      )}
    </ActionModal>
  );
}
